/**
 * GuardianPicker
 *
 * Lightweight controlled-input + result-list picker that surfaces existing
 * roster persons so the parent can attach them as a guardian on an active
 * reunification event via `useCreateEventGuardian`. Mirrors the
 * `usePlanningStudents` pattern (school-scoped roster search, debounced
 * query, capped page size) — guardians (unlike staff) are RosterPersons
 * (ADR-064 §LAYER 3), so the picker pulls from
 * `RosterPersonPlanningViewSet` rather than the auth-user endpoint
 * StaffPicker uses.
 *
 * Scope (v1): searches roster persons with `role='guardian'` in the
 * operator's schools and excludes any RosterPerson already assigned as a
 * guardian on this event. `RosterPersonPlanningViewSet` honours
 * `?role=guardian`, so this picker never mixes students / staff into
 * its results.
 *
 * Out of scope:
 * - **Ad-hoc walk-ins** — creating brand-new RosterPerson rows on the fly
 *   during an event. That is a separate backend + roster-app change with
 *   its own CCPA review.
 * - Photo capture / ID document upload — same gate.
 */

import { useState, useMemo } from 'react';
import {
  Box,
  Center,
  Input,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
import { CardButton } from '@themes/aegix';
import { usePlanningStudents } from '../../hooks/queries/useReunification';
import type { PlanningStudent } from '../../types/ReunificationTypes';

const PICKER_PAGE_SIZE = 20;

export interface GuardianPickerSelection {
  /** Numeric RosterPerson PK — required by GuardianReunification.roster_person. */
  rosterPersonId: number;
  /** UUID public_id of the same row, kept for display / debug. */
  rosterPersonPublicId: string;
  name: string;
  externalReferenceId: string;
}

export interface GuardianPickerProps {
  /** RosterPerson ids already on the event — filtered out of results. */
  excludeRosterPersonIds?: number[];
  /** The currently picked roster person (controlled). */
  selected: GuardianPickerSelection | null;
  /** Called when the user clicks a row. */
  onSelect: (selection: GuardianPickerSelection) => void;
  /** Optional placeholder for the search input. */
  placeholder?: string;
  /** Optional fixed height for the result list (default 240). */
  listHeight?: number;
  /** Optional data-e2e prefix to keep parents' selectors stable. */
  dataE2ePrefix?: string;
}

export function GuardianPicker({
  excludeRosterPersonIds,
  selected,
  onSelect,
  placeholder = 'Search guardians by name…',
  listHeight = 240,
  dataE2ePrefix = 'guardian-picker',
}: GuardianPickerProps) {
  const [search, setSearch] = useState('');

  const { data, isLoading, isFetching, error } = usePlanningStudents({
    search: search || undefined,
    role: 'guardian',
    page_size: PICKER_PAGE_SIZE,
    ordering: 'last_name',
  });

  const excludeSet = useMemo(
    () => new Set(excludeRosterPersonIds ?? []),
    [excludeRosterPersonIds],
  );

  const candidates = useMemo<PlanningStudent[]>(() => {
    const all = data?.results ?? [];
    return all.filter((p) => {
      if (!Number.isFinite(p.id) || p.id <= 0) return false;
      return !excludeSet.has(p.id);
    });
  }, [data?.results, excludeSet]);

  return (
    <VStack align="stretch" gap={3}>
      <Input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder={placeholder}
        aria-label={placeholder.replace(/[….]+$/, '')}
        size="sm"
        data-e2e={`${dataE2ePrefix}-search`}
      />

      <Box
        borderWidth="1px"
        borderColor="border.subtle"
        borderRadius="md"
        height={`${listHeight}px`}
        overflowY="auto"
        data-e2e={`${dataE2ePrefix}-list`}
      >
        {isLoading ? (
          <Center height="100%">
            <Spinner size="sm" color="button.primary" />
          </Center>
        ) : error ? (
          <Center height="100%" px={3}>
            <Text fontSize="sm" color="text.danger">
              {error instanceof Error
                ? error.message
                : 'Failed to load roster'}
            </Text>
          </Center>
        ) : candidates.length === 0 ? (
          <Center height="100%">
            {isFetching ? (
              <Spinner size="sm" color="button.primary" />
            ) : (
              <Text fontSize="sm" color="text.tertiary">
                {search
                  ? 'No matching roster person found'
                  : 'No roster persons available to add'}
              </Text>
            )}
          </Center>
        ) : (
          <VStack align="stretch" gap={0}>
            {candidates.map((person) => {
              const fullName =
                `${person.first_name ?? ''} ${person.last_name ?? ''}`.trim() ||
                person.external_reference_id ||
                person.public_id;
              const isSelected =
                selected?.rosterPersonId === person.id;
              return (
                <CardButton
                  key={person.id}
                  variant="row"
                  selected={isSelected}
                  onClick={() =>
                    onSelect({
                      rosterPersonId: person.id,
                      rosterPersonPublicId: person.public_id,
                      name: fullName,
                      externalReferenceId: person.external_reference_id ?? '',
                    })
                  }
                  description={
                    person.external_reference_id
                      ? `Ref: ${person.external_reference_id}`
                      : undefined
                  }
                  data-e2e={`${dataE2ePrefix}-row-${person.id}`}
                >
                  {fullName}
                </CardButton>
              );
            })}
          </VStack>
        )}
      </Box>

      {selected ? (
        <Text
          fontSize="xs"
          color="text.secondary"
          data-e2e={`${dataE2ePrefix}-selected-summary`}
        >
          Selected: {selected.name}
        </Text>
      ) : null}
    </VStack>
  );
}

export default GuardianPicker;
